import { useState, useCallback } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useProjectStore } from '../stores/projectStore';
import { useProjectNavigation } from './useProjectNavigation';
import { useViewTransition } from './useViewTransition';
import { setPersistedGroups } from './useDataPersistence';
import { Workflow, AppNode } from '../types';

export interface UseWorkflowManagerOptions {
  saveHistory: () => void;
}

export function useWorkflowManager(options: UseWorkflowManagerOptions) {
  const { saveHistory } = options;
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);

  const nodes = useNodeStore((s) => s.nodes);
  const connections = useNodeStore((s) => s.connections);
  const setNodes = useNodeStore((s) => s.setNodes);
  const setConnections = useNodeStore((s) => s.setConnections);
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);

  const {
    loadWorkflow, handleCreateProject, handleBackToProjects,
    deleteWorkflow, renameWorkflow, getThumbnail,
  } = useProjectNavigation(saveHistory);

  const { transitionTo, transitionFrom, finishTransition } = useViewTransition();

  const saveCurrentAsWorkflow = useCallback(() => {
    const thumbnail = getThumbnail();
    const newWf: Workflow = {
      id: `wf-${Date.now()}`,
      title: `工作流 ${new Date().toLocaleDateString()}`,
      thumbnail,
      nodes: JSON.parse(JSON.stringify(nodes)),
      connections: JSON.parse(JSON.stringify(connections)),
      groups: [],
    };
    setWorkflows(prev => [newWf, ...prev]);
  }, [nodes, connections, getThumbnail]);

  const onLoadWorkflow = useCallback(async (id: string) => {
    await loadWorkflow(id, transitionTo);
    setSelectedWorkflowId(id);
    finishTransition();
  }, [loadWorkflow, transitionTo, finishTransition]);

  const onCreateProject = useCallback(async (title: string, setIsInEditor: (v: boolean) => void, setDataLoadSucceeded: (v: boolean) => void) => {
    const project = await handleCreateProject(title, transitionTo);
    if (project) {
      setNodes([]);
      setConnections([]);
      setPersistedGroups([]);
      setSelectedWorkflowId(project.id);
      setIsInEditor(true);
    }
    finishTransition();
  }, [handleCreateProject, transitionTo, setNodes, setConnections, finishTransition]);

  const onBackToProjects = useCallback(async (setIsInEditor: (v: boolean) => void) => {
    setIsInEditor(false);
    setCurrentProject(null);
    finishTransition();
    handleBackToProjects(transitionFrom).catch(e => console.error('Background save failed:', e));
  }, [handleBackToProjects, transitionFrom, setCurrentProject, finishTransition]);

  return {
    workflows,
    setWorkflows,
    selectedWorkflowId,
    saveCurrentAsWorkflow,
    onLoadWorkflow,
    onCreateProject,
    onBackToProjects,
    deleteWorkflow,
    renameWorkflow,
  };
}
